import { Pawn } from '../pieces/Pawn';
import { Rook } from '../pieces/Rook';
import { Knight } from '../pieces/Knight';
import { Bishop } from '../pieces/Bishop';
import { Queen } from '../pieces/Queen';
import { King } from '../pieces/King';

const COLORS = ['white', 'black'];
const PROMOTION_PIECES = ['queen', 'rook', 'bishop', 'knight'];
const FILES = 'abcdefgh';
const PIECE_TYPES_BY_FEN = {
  p: 'pawn',
  n: 'knight',
  b: 'bishop',
  r: 'rook',
  q: 'queen',
  k: 'king',
};
const FEN_SYMBOLS = { pawn: 'p', knight: 'n', bishop: 'b', rook: 'r', queen: 'q', king: 'k' };

export const STANDARD_START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const oppositeColor = (color) => (color === 'white' ? 'black' : 'white');
const samePosition = (a, b) => a && b && a[0] === b[0] && a[1] === b[1];
const inBounds = (row, col) => row >= 1 && row <= 8 && col >= 1 && col <= 8;

export const positionToSquare = ([row, col]) => `${FILES[col - 1]}${9 - row}`;
export const squareToPosition = (square) => {
  if (!/^[a-h][1-8]$/.test(square)) return null;
  return [9 - Number(square[1]), FILES.indexOf(square[0]) + 1];
};

export class GameState {
  constructor() {
    this.board = this.createInitialBoard();
    this.currentPlayer = 'white';
    this.selectedPiece = null;
    this.possibleMoves = [];
    this.moveHistory = [];
    this.lastMove = null;
    this.halfmoveClock = 0;
    this.fullmoveNumber = 1;
    this.status = { type: 'active', message: 'White to move' };
    this.positionCounts = new Map();
    this.recordPosition();
    this.startingFen = STANDARD_START_FEN;
    this.startingPlayer = 'white';
    this.startingFullmoveNumber = 1;
  }

  createInitialBoard() {
    const board = Array.from({ length: 9 }, () => Array(9).fill(null));
    const backRank = ['rook', 'knight', 'bishop', 'queen', 'king', 'bishop', 'knight', 'rook'];

    backRank.forEach((type, index) => {
      const col = index + 1;
      board[1][col] = { type, color: 'black', hasMoved: false };
      board[2][col] = { type: 'pawn', color: 'black', hasMoved: false };
      board[7][col] = { type: 'pawn', color: 'white', hasMoved: false };
      board[8][col] = { type, color: 'white', hasMoved: false };
    });

    return board;
  }

  getPiece(position, board = this.board) {
    const [row, col] = position;
    if (!inBounds(row, col)) return null;
    return board[row][col];
  }

  isOccupied(position, board = this.board) {
    return this.getPiece(position, board) !== null;
  }

  getPieceColor(position, board = this.board) {
    return this.getPiece(position, board)?.color ?? null;
  }

  getPseudoLegalMoves(position, includeCastling = true) {
    const piece = this.getPiece(position);
    if (!piece) return [];

    let moves;
    switch (piece.type) {
      case 'pawn':
        moves = Pawn.getMoves(position, piece.color, this);
        break;
      case 'rook':
        moves = Rook.getMoves(position, piece.color, this);
        break;
      case 'knight':
        moves = Knight.getMoves(position, piece.color, this);
        break;
      case 'bishop':
        moves = Bishop.getMoves(position, piece.color, this);
        break;
      case 'queen':
        moves = Queen.getMoves(position, piece.color, this);
        break;
      case 'king':
        moves = King.getMoves(position, piece.color, this);
        if (includeCastling) {
          if (this.canCastle(piece.color, 'king')) moves.push([position[0], 7]);
          if (this.canCastle(piece.color, 'queen')) moves.push([position[0], 3]);
        }
        break;
      default:
        return [];
    }

    return moves.filter((target) => this.getPiece(target)?.type !== 'king');
  }

  getLegalMoves(position) {
    const piece = this.getPiece(position);
    if (!piece) return [];

    return this.getPseudoLegalMoves(position).filter((target) => {
      const simulatedBoard = this.simulateMove(position, target);
      return !this.isInCheck(piece.color, simulatedBoard);
    });
  }

  getPossibleMoves(position) {
    const piece = this.getPiece(position);
    if (!piece || piece.color !== this.currentPlayer || this.isGameOver()) return [];
    return this.getLegalMoves(position);
  }

  selectPiece(position) {
    const piece = this.getPiece(position);
    if (!piece || piece.color !== this.currentPlayer || this.isGameOver()) return false;

    this.selectedPiece = [...position];
    this.possibleMoves = this.getLegalMoves(position);
    return true;
  }

  clearSelection() {
    this.selectedPiece = null;
    this.possibleMoves = [];
  }

  movePiece(toPosition, promotionType = 'queen') {
    if (!this.selectedPiece || this.isGameOver()) return false;
    if (!this.possibleMoves.some((move) => samePosition(move, toPosition))) return false;

    const fromPosition = [...this.selectedPiece];
    const [fromRow, fromCol] = fromPosition;
    const [toRow, toCol] = toPosition;
    const movingPiece = this.board[fromRow][fromCol];
    const originalType = movingPiece.type;
    const isPawnMove = originalType === 'pawn';
    const isEnPassant = isPawnMove && fromCol !== toCol && !this.board[toRow][toCol];
    const capturedPosition = isEnPassant ? [fromRow, toCol] : [toRow, toCol];
    const capturedPiece = this.getPiece(capturedPosition);
    const isCastling = originalType === 'king' && Math.abs(toCol - fromCol) === 2;
    const disambiguation = this.getMoveDisambiguation(fromPosition, toPosition, movingPiece);

    this.board[toRow][toCol] = { ...movingPiece, hasMoved: true };
    this.board[fromRow][fromCol] = null;

    if (isEnPassant) this.board[capturedPosition[0]][capturedPosition[1]] = null;

    if (isCastling) {
      const rookFromCol = toCol === 7 ? 8 : 1;
      const rookToCol = toCol === 7 ? 6 : 4;
      const rook = this.board[fromRow][rookFromCol];
      this.board[fromRow][rookToCol] = { ...rook, hasMoved: true };
      this.board[fromRow][rookFromCol] = null;
    }

    let promotion = null;
    if (isPawnMove && (toRow === 1 || toRow === 8)) {
      promotion = PROMOTION_PIECES.includes(promotionType) ? promotionType : 'queen';
      this.board[toRow][toCol].type = promotion;
    }

    this.halfmoveClock = isPawnMove || capturedPiece ? 0 : this.halfmoveClock + 1;
    const move = {
      from: fromPosition,
      to: [...toPosition],
      color: movingPiece.color,
      piece: originalType,
      captured: capturedPiece?.type ?? null,
      isEnPassant,
      isCastling,
      promotion,
      disambiguation,
    };

    this.lastMove = move;
    this.currentPlayer = oppositeColor(this.currentPlayer);
    if (movingPiece.color === 'black') this.fullmoveNumber += 1;
    this.clearSelection();
    move.epd = this.getEpd();
    this.recordPosition();
    this.updateStatus();

    move.notation = this.createNotation(move);
    this.moveHistory.push(move);
    return true;
  }

  createNotation(move) {
    const symbols = { king: 'K', queen: 'Q', rook: 'R', bishop: 'B', knight: 'N', pawn: '' };
    const suffix = this.status.type === 'checkmate' ? '#' : this.status.type === 'check' ? '+' : '';
    if (move.isCastling) return `${move.to[1] === 7 ? 'O-O' : 'O-O-O'}${suffix}`;

    const pawnCaptureFile = move.piece === 'pawn' && move.captured ? FILES[move.from[1] - 1] : '';
    const capture = move.captured ? 'x' : '';
    const promotion = move.promotion ? `=${symbols[move.promotion]}` : '';
    return `${symbols[move.piece]}${move.disambiguation ?? ''}${pawnCaptureFile}${capture}${positionToSquare(move.to)}${promotion}${suffix}`;
  }

  getMoveDisambiguation(fromPosition, toPosition, movingPiece) {
    if (!movingPiece || ['pawn', 'king'].includes(movingPiece.type)) return '';

    const alternatives = [];
    for (let row = 1; row <= 8; row += 1) {
      for (let col = 1; col <= 8; col += 1) {
        if (samePosition([row, col], fromPosition)) continue;
        const piece = this.board[row][col];
        if (piece?.type !== movingPiece.type || piece.color !== movingPiece.color) continue;
        if (this.getLegalMoves([row, col]).some((target) => samePosition(target, toPosition))) alternatives.push([row, col]);
      }
    }

    if (alternatives.length === 0) return '';
    const fileIsUnique = alternatives.every((position) => position[1] !== fromPosition[1]);
    if (fileIsUnique) return FILES[fromPosition[1] - 1];
    const rankIsUnique = alternatives.every((position) => position[0] !== fromPosition[0]);
    if (rankIsUnique) return String(9 - fromPosition[0]);
    return positionToSquare(fromPosition);
  }

  simulateMove(fromPosition, toPosition) {
    const board = this.board.map((row) => row.map((cell) => (cell ? { ...cell } : null)));
    const [fromRow, fromCol] = fromPosition;
    const [toRow, toCol] = toPosition;
    const piece = board[fromRow][fromCol];

    if (piece.type === 'pawn' && fromCol !== toCol && !board[toRow][toCol]) {
      board[fromRow][toCol] = null;
    }

    board[toRow][toCol] = { ...piece, hasMoved: true };
    board[fromRow][fromCol] = null;

    if (piece.type === 'king' && Math.abs(toCol - fromCol) === 2) {
      const rookFromCol = toCol === 7 ? 8 : 1;
      const rookToCol = toCol === 7 ? 6 : 4;
      board[fromRow][rookToCol] = { ...board[fromRow][rookFromCol], hasMoved: true };
      board[fromRow][rookFromCol] = null;
    }

    return board;
  }

  findKing(color, board = this.board) {
    for (let row = 1; row <= 8; row += 1) {
      for (let col = 1; col <= 8; col += 1) {
        const piece = board[row][col];
        if (piece?.type === 'king' && piece.color === color) return [row, col];
      }
    }
    return null;
  }

  isSquareAttacked(position, byColor, board = this.board) {
    const [row, col] = position;
    const pawnSourceRow = row + (byColor === 'white' ? 1 : -1);

    for (const sourceCol of [col - 1, col + 1]) {
      const piece = this.getPiece([pawnSourceRow, sourceCol], board);
      if (piece?.color === byColor && piece.type === 'pawn') return true;
    }

    const knightOffsets = [[2, 1], [2, -1], [-2, 1], [-2, -1], [1, 2], [1, -2], [-1, 2], [-1, -2]];
    if (knightOffsets.some(([dr, dc]) => {
      const piece = this.getPiece([row + dr, col + dc], board);
      return piece?.color === byColor && piece.type === 'knight';
    })) return true;

    for (let dr = -1; dr <= 1; dr += 1) {
      for (let dc = -1; dc <= 1; dc += 1) {
        if (dr === 0 && dc === 0) continue;
        const piece = this.getPiece([row + dr, col + dc], board);
        if (piece?.color === byColor && piece.type === 'king') return true;
      }
    }

    const rays = [
      [1, 0, ['rook', 'queen']], [-1, 0, ['rook', 'queen']],
      [0, 1, ['rook', 'queen']], [0, -1, ['rook', 'queen']],
      [1, 1, ['bishop', 'queen']], [1, -1, ['bishop', 'queen']],
      [-1, 1, ['bishop', 'queen']], [-1, -1, ['bishop', 'queen']],
    ];

    for (const [dr, dc, attackers] of rays) {
      let targetRow = row + dr;
      let targetCol = col + dc;
      while (inBounds(targetRow, targetCol)) {
        const piece = board[targetRow][targetCol];
        if (piece) {
          if (piece.color === byColor && attackers.includes(piece.type)) return true;
          break;
        }
        targetRow += dr;
        targetCol += dc;
      }
    }

    return false;
  }

  isInCheck(color, board = this.board) {
    const kingPosition = this.findKing(color, board);
    return Boolean(kingPosition && this.isSquareAttacked(kingPosition, oppositeColor(color), board));
  }

  canCastle(color, side) {
    const row = color === 'white' ? 8 : 1;
    const king = this.board[row][5];
    const rookCol = side === 'king' ? 8 : 1;
    const rook = this.board[row][rookCol];
    if (king?.type !== 'king' || king.color !== color || king.hasMoved) return false;
    if (rook?.type !== 'rook' || rook.color !== color || rook.hasMoved) return false;

    const clearCols = side === 'king' ? [6, 7] : [2, 3, 4];
    if (clearCols.some((col) => this.board[row][col])) return false;

    const safeCols = side === 'king' ? [5, 6, 7] : [5, 4, 3];
    return safeCols.every((col) => !this.isSquareAttacked([row, col], oppositeColor(color)));
  }

  canEnPassant(fromPosition, toPosition, color) {
    if (!this.lastMove || this.lastMove.piece !== 'pawn') return false;
    const [fromRow, fromCol] = fromPosition;
    const [toRow, toCol] = toPosition;
    const direction = color === 'white' ? -1 : 1;
    if (toRow !== fromRow + direction || Math.abs(toCol - fromCol) !== 1) return false;
    if (this.getPiece(toPosition)) return false;
    if (this.lastMove.color === color || Math.abs(this.lastMove.from[0] - this.lastMove.to[0]) !== 2) return false;
    if (!samePosition(this.lastMove.to, [fromRow, toCol])) return false;

    const adjacentPawn = this.getPiece([fromRow, toCol]);
    return adjacentPawn?.type === 'pawn' && adjacentPawn.color !== color;
  }

  hasAnyLegalMove(color) {
    for (let row = 1; row <= 8; row += 1) {
      for (let col = 1; col <= 8; col += 1) {
        if (this.board[row][col]?.color === color && this.getLegalMoves([row, col]).length > 0) return true;
      }
    }
    return false;
  }

  isInsufficientMaterial() {
    const material = [];
    for (let row = 1; row <= 8; row += 1) {
      for (let col = 1; col <= 8; col += 1) {
        const piece = this.board[row][col];
        if (piece && piece.type !== 'king') material.push({ ...piece, squareColor: (row + col) % 2 });
      }
    }

    if (material.length === 0) return true;
    if (material.length === 1) return ['bishop', 'knight'].includes(material[0].type);
    return material.every((piece) => piece.type === 'bishop')
      && new Set(material.map((piece) => piece.squareColor)).size === 1;
  }

  getEnPassantKey() {
    if (!this.lastMove || this.lastMove.piece !== 'pawn' || Math.abs(this.lastMove.from[0] - this.lastMove.to[0]) !== 2) return '-';
    const [row, col] = this.lastMove.to;
    const capturerColor = oppositeColor(this.lastMove.color);
    const targetPosition = [(this.lastMove.from[0] + this.lastMove.to[0]) / 2, col];
    const hasLegalCapturer = [col - 1, col + 1].some((sourceCol) => {
      const sourcePosition = [row, sourceCol];
      const piece = this.getPiece(sourcePosition);
      if (piece?.type !== 'pawn' || piece.color !== capturerColor) return false;
      if (!this.canEnPassant(sourcePosition, targetPosition, capturerColor)) return false;
      return !this.isInCheck(capturerColor, this.simulateMove(sourcePosition, targetPosition));
    });
    return hasLegalCapturer ? positionToSquare(targetPosition) : '-';
  }

  getEpd() {
    const ranks = [];

    for (let row = 1; row <= 8; row += 1) {
      let rank = '';
      let emptySquares = 0;
      for (let col = 1; col <= 8; col += 1) {
        const piece = this.board[row][col];
        if (!piece) {
          emptySquares += 1;
          continue;
        }
        if (emptySquares > 0) {
          rank += emptySquares;
          emptySquares = 0;
        }
        const symbol = FEN_SYMBOLS[piece.type];
        rank += piece.color === 'white' ? symbol.toUpperCase() : symbol;
      }
      if (emptySquares > 0) rank += emptySquares;
      ranks.push(rank);
    }

    const castlingRights = [
      this.hasCastlingRight('white', 'king') ? 'K' : '',
      this.hasCastlingRight('white', 'queen') ? 'Q' : '',
      this.hasCastlingRight('black', 'king') ? 'k' : '',
      this.hasCastlingRight('black', 'queen') ? 'q' : '',
    ].join('') || '-';
    const activeColor = this.currentPlayer === 'white' ? 'w' : 'b';
    return `${ranks.join('/')} ${activeColor} ${castlingRights} ${this.getEnPassantKey()}`;
  }

  getFen() {
    return `${this.getEpd()} ${this.halfmoveClock} ${this.fullmoveNumber}`;
  }

  getResult() {
    if (this.status.winner === 'white') return '1-0';
    if (this.status.winner === 'black') return '0-1';
    if (['stalemate', 'draw'].includes(this.status.type)) return '1/2-1/2';
    return '*';
  }

  toPgn(metadata = {}) {
    const result = this.getResult();
    const escapeTag = (value) => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const tags = [
      ['Event', metadata.event ?? 'Quiet Board Game'],
      ['Site', metadata.site ?? 'Local'],
      ['Date', metadata.date ?? '????.??.??'],
      ['Round', metadata.round ?? '-'],
      ['White', metadata.white ?? 'White'],
      ['Black', metadata.black ?? 'Black'],
      ['Result', result],
    ];

    if (metadata.opening) tags.push(['Opening', metadata.opening]);
    if (metadata.eco) tags.push(['ECO', metadata.eco]);
    if (this.startingFen !== STANDARD_START_FEN) {
      tags.push(['SetUp', '1']);
      tags.push(['FEN', this.startingFen]);
    }

    const tokens = [];
    let moveNumber = this.startingFullmoveNumber;
    this.moveHistory.forEach((move, index) => {
      if (move.color === 'white') {
        tokens.push(`${moveNumber}.`, move.notation);
      } else {
        if (index === 0 || this.moveHistory[index - 1].color !== 'white') tokens.push(`${moveNumber}...`);
        tokens.push(move.notation);
        moveNumber += 1;
      }
    });
    tokens.push(result);

    const header = tags.map(([name, value]) => `[${name} "${escapeTag(value)}"]`).join('\n');
    return `${header}\n\n${tokens.join(' ')}`;
  }

  static fromFen(fen) {
    if (typeof fen !== 'string' || fen.trim() === '') throw new Error('Enter a FEN position.');
    const fields = fen.trim().split(/\s+/);
    if (fields.length < 4 || fields.length > 6) throw new Error('FEN must contain 4 to 6 fields.');

    const [placement, activeColor, castling, enPassant] = fields;
    const halfmove = fields[4] ?? '0';
    const fullmove = fields[5] ?? '1';
    if (!['w', 'b'].includes(activeColor)) throw new Error('Active color must be w or b.');
    if (castling !== '-' && (!/^[KQkq]+$/.test(castling) || new Set(castling).size !== castling.length)) {
      throw new Error('Castling rights are invalid.');
    }
    if (enPassant !== '-' && !/^[a-h][36]$/.test(enPassant)) throw new Error('En passant square is invalid.');
    if (!/^\d+$/.test(halfmove) || !/^\d+$/.test(fullmove) || Number(fullmove) < 1) {
      throw new Error('Move counters are invalid.');
    }

    const ranks = placement.split('/');
    if (ranks.length !== 8) throw new Error('Piece placement must contain 8 ranks.');
    const board = Array.from({ length: 9 }, () => Array(9).fill(null));
    const kingCounts = { white: 0, black: 0 };

    ranks.forEach((rank, rankIndex) => {
      let col = 1;
      for (const symbol of rank) {
        if (/^[1-8]$/.test(symbol)) {
          col += Number(symbol);
          continue;
        }
        const type = PIECE_TYPES_BY_FEN[symbol.toLowerCase()];
        if (!type || col > 8) throw new Error(`Rank ${8 - rankIndex} contains an invalid piece layout.`);
        const color = symbol === symbol.toUpperCase() ? 'white' : 'black';
        if (type === 'pawn' && (rankIndex === 0 || rankIndex === 7)) throw new Error('Pawns cannot be placed on the first or eighth rank.');
        board[rankIndex + 1][col] = { type, color, hasMoved: true };
        if (type === 'king') kingCounts[color] += 1;
        col += 1;
      }
      if (col !== 9) throw new Error(`Rank ${8 - rankIndex} must contain exactly 8 squares.`);
    });

    if (kingCounts.white !== 1 || kingCounts.black !== 1) throw new Error('The position must contain exactly one king for each side.');

    const requireCastlingPiece = (right, row, rookCol, color) => {
      if (!castling.includes(right)) return;
      if (board[row][5]?.type !== 'king' || board[row][5]?.color !== color
        || board[row][rookCol]?.type !== 'rook' || board[row][rookCol]?.color !== color) {
        throw new Error(`Castling right ${right} does not match the king and rook placement.`);
      }
      board[row][5].hasMoved = false;
      board[row][rookCol].hasMoved = false;
    };
    requireCastlingPiece('K', 8, 8, 'white');
    requireCastlingPiece('Q', 8, 1, 'white');
    requireCastlingPiece('k', 1, 8, 'black');
    requireCastlingPiece('q', 1, 1, 'black');

    const state = new GameState();
    state.board = board;
    state.currentPlayer = activeColor === 'w' ? 'white' : 'black';
    state.selectedPiece = null;
    state.possibleMoves = [];
    state.moveHistory = [];
    state.lastMove = null;
    state.halfmoveClock = Number(halfmove);
    state.fullmoveNumber = Number(fullmove);

    if (enPassant !== '-') {
      const target = squareToPosition(enPassant);
      const expectedRank = state.currentPlayer === 'white' ? '6' : '3';
      if (enPassant[1] !== expectedRank) throw new Error('En passant square does not match the active color.');
      const pawnColor = oppositeColor(state.currentPlayer);
      const fromRow = pawnColor === 'white' ? 7 : 2;
      const toRow = pawnColor === 'white' ? 5 : 4;
      const pawn = board[toRow][target[1]];
      if (pawn?.type !== 'pawn' || pawn.color !== pawnColor) throw new Error('En passant square has no matching pawn move.');
      state.lastMove = {
        from: [fromRow, target[1]],
        to: [toRow, target[1]],
        color: pawnColor,
        piece: 'pawn',
        captured: null,
        isEnPassant: false,
        isCastling: false,
        promotion: null,
      };
    }

    if (state.isInCheck('white') && state.isInCheck('black')) throw new Error('Both kings cannot be in check.');
    state.positionCounts = new Map();
    state.recordPosition();
    state.updateStatus();
    state.startingFen = state.getFen();
    state.startingPlayer = state.currentPlayer;
    state.startingFullmoveNumber = state.fullmoveNumber;
    return state;
  }

  getPositionKey() {
    const pieces = [];
    for (let row = 1; row <= 8; row += 1) {
      for (let col = 1; col <= 8; col += 1) {
        const piece = this.board[row][col];
        pieces.push(piece ? `${piece.color[0]}${piece.type[0]}${piece.type === 'knight' ? 'n' : ''}` : '.');
      }
    }

    const castlingRights = COLORS.flatMap((color) => {
      const prefix = color === 'white' ? '' : 'b';
      return [this.hasCastlingRight(color, 'king') ? `${prefix}K` : '', this.hasCastlingRight(color, 'queen') ? `${prefix}Q` : ''];
    }).join('') || '-';

    return `${pieces.join('')}|${this.currentPlayer}|${castlingRights}|${this.getEnPassantKey()}`;
  }

  hasCastlingRight(color, side) {
    const row = color === 'white' ? 8 : 1;
    const rookCol = side === 'king' ? 8 : 1;
    const king = this.board[row][5];
    const rook = this.board[row][rookCol];
    return king?.type === 'king' && king.color === color && !king.hasMoved
      && rook?.type === 'rook' && rook.color === color && !rook.hasMoved;
  }

  recordPosition() {
    const key = this.getPositionKey();
    this.positionCounts.set(key, (this.positionCounts.get(key) ?? 0) + 1);
  }

  updateStatus() {
    const inCheck = this.isInCheck(this.currentPlayer);
    const hasMove = this.hasAnyLegalMove(this.currentPlayer);
    const playerName = this.currentPlayer === 'white' ? 'White' : 'Black';

    if (!hasMove) {
      if (inCheck) {
        const winner = oppositeColor(this.currentPlayer);
        this.status = { type: 'checkmate', winner, message: `Checkmate — ${winner === 'white' ? 'White' : 'Black'} wins` };
      } else {
        this.status = { type: 'stalemate', reason: 'stalemate', message: 'Draw by stalemate' };
      }
      return;
    }

    if ((this.positionCounts.get(this.getPositionKey()) ?? 0) >= 3) {
      this.status = { type: 'draw', reason: 'threefold repetition', message: 'Draw by threefold repetition' };
    } else if (this.halfmoveClock >= 100) {
      this.status = { type: 'draw', reason: 'fifty-move rule', message: 'Draw by the fifty-move rule' };
    } else if (this.isInsufficientMaterial()) {
      this.status = { type: 'draw', reason: 'insufficient material', message: 'Draw by insufficient material' };
    } else if (inCheck) {
      this.status = { type: 'check', message: `${playerName} is in check — answer the threat` };
    } else {
      this.status = { type: 'active', message: `${playerName} to move` };
    }
  }

  isGameOver() {
    return ['checkmate', 'stalemate', 'draw', 'timeout'].includes(this.status.type);
  }

  getStateAtPly(requestedPly) {
    const numericPly = Number.isFinite(Number(requestedPly)) ? Math.trunc(Number(requestedPly)) : 0;
    const ply = Math.min(this.moveHistory.length, Math.max(0, numericPly));

    if (ply === this.moveHistory.length) {
      const finalState = this.clone();
      finalState.clearSelection();
      return finalState;
    }

    const reviewState = GameState.fromFen(this.startingFen);
    for (const move of this.moveHistory.slice(0, ply)) {
      if (!reviewState.selectPiece(move.from) || !reviewState.movePiece(move.to, move.promotion ?? 'queen')) {
        throw new Error(`Could not reconstruct move ${reviewState.moveHistory.length + 1}.`);
      }
    }
    reviewState.clearSelection();
    return reviewState;
  }

  getBoard() {
    return this.board;
  }

  clone() {
    const newState = new GameState();
    newState.board = this.board.map((row) => row.map((cell) => (cell ? { ...cell } : null)));
    newState.currentPlayer = this.currentPlayer;
    newState.selectedPiece = this.selectedPiece ? [...this.selectedPiece] : null;
    newState.possibleMoves = this.possibleMoves.map((move) => [...move]);
    newState.moveHistory = this.moveHistory.map((move) => ({
      ...move,
      from: [...move.from],
      to: [...move.to],
    }));
    newState.lastMove = this.lastMove ? { ...this.lastMove, from: [...this.lastMove.from], to: [...this.lastMove.to] } : null;
    newState.halfmoveClock = this.halfmoveClock;
    newState.fullmoveNumber = this.fullmoveNumber;
    newState.status = { ...this.status };
    newState.positionCounts = new Map(this.positionCounts);
    newState.startingFen = this.startingFen;
    newState.startingPlayer = this.startingPlayer;
    newState.startingFullmoveNumber = this.startingFullmoveNumber;
    return newState;
  }
}
